import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Eye, Pencil, Trash2, ArrowUpDown } from 'lucide-react';
import { FlexESimulationSummary } from '@/hooks/useFlexESimulations';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type SortKey = 'created_at' | 'name' | 'success_rate';
type SortOrder = 'asc' | 'desc';

interface FlexEHistoryTableProps {
  simulations: FlexESimulationSummary[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onView: (sim: FlexESimulationSummary) => void;
  onRename: (sim: FlexESimulationSummary) => void;
  onDelete: (simId: string) => void;
  isDeleting: boolean;
}

export default function FlexEHistoryTable({
  simulations,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onView,
  onRename,
  onDelete,
  isDeleting
}: FlexEHistoryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [deleteSimId, setDeleteSimId] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const sortedSimulations = [...simulations].sort((a, b) => {
    let aVal: any = a[sortKey];
    let bVal: any = b[sortKey];

    if (sortKey === 'created_at') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const allSelected = simulations.length > 0 && selectedIds.length === simulations.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < simulations.length;

  const getSuccessRateColor = (rate: number | null) => {
    if (rate === null) return 'bg-muted';
    if (rate >= 95) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (rate >= 80) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  if (simulations.length === 0) {
    return (
      <Card className="p-12">
        <div className="text-center space-y-3">
          <p className="text-lg text-muted-foreground">Nog geen simulaties</p>
          <p className="text-sm text-muted-foreground">
            Upload een bestand en voer je eerste simulatie uit
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected || someSelected}
                  onCheckedChange={onToggleSelectAll}
                  aria-label="Selecteer alles"
                />
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('name')}
                  className="h-8 px-2"
                >
                  Naam
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('created_at')}
                  className="h-8 px-2"
                >
                  Datum
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('success_rate')}
                  className="h-8 px-2"
                >
                  Success Rate
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead>Dagen</TableHead>
              <TableHead>Configuratie</TableHead>
              <TableHead className="text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSimulations.map((sim) => (
              <TableRow
                key={sim.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onView(sim)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(sim.id)}
                    onCheckedChange={() => onToggleSelect(sim.id)}
                    aria-label={`Selecteer ${sim.name}`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {sim.name || 'Naamloos'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(sim.created_at).toLocaleDateString('nl-NL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </TableCell>
                <TableCell>
                  <Badge className={getSuccessRateColor(sim.success_rate || 0)}>
                    {sim.success_rate?.toFixed(1) || 0}%
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {sim.passed_days || 0}
                  </span>
                  {' / '}
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    {sim.failed_days || 0}
                  </span>
                  <span className="text-muted-foreground">
                    {' '}({sim.total_days || 0})
                  </span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {sim.batt_kw}kW / {sim.batt_kwh?.toFixed(0)}kWh
                  <br />
                  Limiet: {sim.cap_limited_kw}kW
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onView(sim)}
                      title="Bekijken"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onRename(sim)}
                      title="Hernoemen"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteSimId(sim.id)}
                      title="Verwijderen"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!deleteSimId} onOpenChange={() => setDeleteSimId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Simulatie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan worden gemaakt. De simulatie en alle bijbehorende resultaten worden permanent verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteSimId) onDelete(deleteSimId);
                setDeleteSimId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
